import React, { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { Table, Button, Dialog, Message, Tab } from '@alifd/next';
import { Form, Input, Select, InputNumber, Radio } from 'antd';
import moment from 'moment';
import PageHeader from '@/components/PageHeader';
import client from '@/lib/feathers';
import { useSelector } from 'react-redux';
import EditService from '@/components/UI/EditService';
import QuestionnaireSettings from './components/QuestionnaireSettings';
import Swal from 'sweetalert2';
import { RootState } from '@/redux/store';
import { MenuCoVendor } from '@/types/database/menuCoVendor';
import QuestionnaireType from '@/types/database/questionnaire';

const Column = Table.Column;
const Item = Form.Item;

const questionnaire = client.service('questionnaire');

type QuestionnaireOptionsType = MenuCoVendor & {
  table: string;
};

export default function Questionnaire({ showHeader }) {
  const [isUpdate, setIsUpdate] = useState(false);
  const [dataSource, setDataSource] = useState<QuestionnaireType[]>([]);
  const [visible, setVisible] = useState(false);
  const [editType, setEditType] = useState('create');
  const [formValues, setFormValues] = useState<QuestionnaireType>();
  const [questionnaireOptions, setQuestionnaireOptions] = useState<{
    companies: QuestionnaireOptionsType[];
  }>({
    companies: [],
  });
  const [currentCompany, setCurrentCompany] =
    useState<QuestionnaireOptionsType>();
  const [filter, setFilter] = useState<{
    filler: string;
    year?: string;
    month?: string;
  }>({
    filler: 'byPerson',
  });

  const [form] = Form.useForm();

  const serviceData = useSelector(
    (state: RootState) => state.service.serviceData
  );
  const [editable, setEditable] = useState(false);

  // 取得問卷資料
  useEffect(() => {
    (async () => {
      const { data } = await questionnaire.find({});
      setDataSource(data);
    })();
    setIsUpdate(false);
  }, [isUpdate]);
  // 取得已開放功能和廠商名單
  useEffect(() => {
    // 取得廠商名單
    const menuCoVendor = client.service('menuCoVendor');
    const menuPropertyVendor = client.service('menuPropertyVendor');
    const venders: QuestionnaireOptionsType[] = [];
    (async () => {
      const menuCoVendorData = await menuCoVendor.find({
        query: {
          $select: ['_id', 'name', 'service'],
          activated: true,
        },
      });
      const menuPropertyVendorData = await menuPropertyVendor.find({
        query: {
          $select: ['_id', 'name', 'service'],
          activated: true,
        },
      });
      venders.push(
        ...menuCoVendorData.data.map((i: MenuCoVendor) => ({
          _id: i._id,
          name: i.name,
          table: 'menuCoVendor',
          service: i.service,
        }))
      );
      venders.push(
        ...menuPropertyVendorData.data.map((i: MenuCoVendor) => ({
          _id: i._id,
          name: i.name,
          table: 'menuPropertyVendor',
          service: i.service,
        }))
      );
      setQuestionnaireOptions((prev) => ({
        ...prev,
        companies: venders,
      }));
    })();
  }, []);

  // 建立問卷
  const createQuestionnaire = async () => {
    if (editType === 'create') {
      try {
        await form.validateFields();
        const values = form.getFieldsValue();
        values.companyTableName =
          questionnaireOptions.companies.find((c) => c._id === values.companyId)
            ?.table || '';
        await questionnaire.create(values);
        Message.success('問卷建立成功');
        setVisible(false);
        setIsUpdate(true);
        form.resetFields();
        setFormValues(undefined);
      } catch (e) {
        console.error(e);
        Message.error('問卷建立失敗');
      }
    } else {
      try {
        if (formValues) {
          await form.validateFields();
          await questionnaire.patch(formValues._id, form.getFieldsValue());
          Message.success('問卷更新成功');
          setVisible(false);
          setIsUpdate(true);
          form.resetFields();
          setFormValues(undefined);
        }
      } catch (e) {
        console.error(e);
        Message.error('問卷更新失敗');
      }
    }
  };
  useEffect(() => {
    form.setFieldsValue(formValues);
  }, [form, formValues]);

  const handleFormFieldChange = ([field]) => {
    if (field.name[0] === 'weights') {
      let target = '';
      switch (field.name[1]) {
        case 'resident':
          target = 'committee';
          break;
        case 'committee':
          target = 'resident';
          break;
        default:
          break;
      }
      if (target) {
        form.setFieldValue(
          ['weights', target],
          Number((100 - field.value).toFixed(1))
        );
      }
    }
  };

  const handleDelete = useCallback(async (id) => {
    const answer = await Swal.fire({
      title: '確定刪除？',
      text: '刪除後將無法復原',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: '確定',
      cancelButtonText: '取消',
    });
    if (answer.isConfirmed) {
      try {
        await questionnaire.remove(id);
        Message.success('刪除成功');
        setIsUpdate(true);
      } catch (err) {
        console.error(err);
      }
    }
  }, []);

  return (
    // 外容器
    <Box>
      {/* 麵包屑與標題 */}
      {showHeader !== false && (
        <PageHeader
          breadcrumbs={[{ name: '問卷管理' }, { name: '問卷列表' }]}
          title="問卷列表"
        />
      )}
      {/* 問卷列表 */}
      {/* 按鈕區 */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
          marginBottom: '10px',
        }}
      >
        <Button
          type="normal"
          onClick={() => {
            setEditType('create');
            setVisible(true);
            setEditable(true);
            form.resetFields();
          }}
        >
          新增問卷
        </Button>
        {/* 問卷設定功能 */}
        <QuestionnaireSettings setIsUpdate={setIsUpdate} />
        <EditService />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '10px',
          alignItems: 'center',
          justifyContent: 'start',
        }}
      >
        <Tab
          style={{
            width: 'fit-content',
          }}
          shape="text"
          activeKey={filter.filler}
          onChange={(val: string) =>
            setFilter((prev) => ({ ...prev, filler: val }))
          }
        >
          <Tab.Item title="一人一票" key="byPerson" />
          <Tab.Item title="一戶一票" key="byHouse" />
        </Tab>
        <Select
          style={{ width: 150 }}
          allowClear
          placeholder="選擇年份"
          value={filter.year}
          onChange={(val) => setFilter((prev) => ({ ...prev, year: val }))}
          options={[
            ...new Set(
              dataSource
                .filter((data) => data.filler === filter.filler)
                .map((i) => i.year)
            ),
          ]
            .map((year) => ({
              label: year,
              value: year,
            }))
            .sort((a, b) => b.value.localeCompare(a.value))}
        />
        <Select
          allowClear
          style={{ width: 150 }}
          placeholder="選擇月份"
          value={filter.month}
          onChange={(val) => setFilter((prev) => ({ ...prev, month: val }))}
          options={[
            ...new Set(
              dataSource
                .filter((data) => data.filler === filter.filler)
                .map((data) => data.month)
            ),
          ]
            .sort((a, b) => a.localeCompare(b))
            .map((month) => ({
              label: month,
              value: month,
            }))}
        />
      </Box>
      <Box
        sx={{
          border: 'none',
          borderRadius: '3px',
          padding: '20px',
          backgroundColor: '#fff',
        }}
      >
        <Table
          dataSource={dataSource.filter((data) => {
            if (data.year !== filter.year) {
              return false;
            }
            if (data.month !== filter.month) {
              return false;
            }
            // if (data.filler !== filter) {
            //   return false;
            // }
            return true;
          })}
        >
          <Column title="年份" dataIndex="year" width="100px" />
          <Column title="月份" dataIndex="month" width="100px" />
          <Column
            title="服務類別"
            width="200px"
            dataIndex="service"
            cell={(service) =>
              serviceData.data.find((data) => data.id === service)?.name || ''
            }
          />
          <Column
            title="負責單位"
            width="200px"
            dataIndex="companyId"
            cell={(val) =>
              questionnaireOptions.companies.find((c) => c._id === val)?.name
            }
          />
          <Column
            title="更新時間"
            width="200px"
            dataIndex="createdTime"
            cell={(value) => moment(value).format('YYYY-MM-DD HH:mm')}
          />
          <Column
            title="操作"
            cell={(_value: never, _index: never, record: QuestionnaireType) => {
              const { _id, activated, year, month } = record;
              const isCurrent =
                moment().add(1, 'month').format('YYYY-MM') ===
                `${year}-${month}`;
              return (
                <Box
                  sx={{
                    display: 'flex',
                    gap: '10px',
                  }}
                >
                  {!isCurrent && (
                    <Button
                      onClick={() => {
                        // 編輯問卷
                        setEditType('patch');
                        setCurrentCompany(
                          questionnaireOptions.companies.find(
                            (company) => company._id === record.companyId
                          )
                        );
                        setFormValues(record);
                        setVisible(true);
                        setEditable(false);
                      }}
                    >
                      查看
                    </Button>
                  )}
                  {isCurrent && (
                    <>
                      {activated ? (
                        <>
                          <Button
                            type="primary"
                            onClick={() => {
                              // 編輯問卷
                              setEditType('patch');
                              setCurrentCompany(
                                questionnaireOptions.companies.find(
                                  (company) => company._id === record.companyId
                                )
                              );
                              setFormValues(record);
                              setVisible(true);
                              setEditable(true);
                            }}
                          >
                            編輯
                          </Button>
                          <Button
                            warning
                            onClick={async () => {
                              // 停用問卷
                              try {
                                await questionnaire.patch(_id, {
                                  activated: false,
                                });
                                Message.success('問卷停用成功');
                                setIsUpdate(true);
                              } catch (e) {
                                console.error(e);
                                Message.error('問卷停用失敗');
                              }
                            }}
                          >
                            停用
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="primary"
                            onClick={async () => {
                              // 啟用問卷
                              try {
                                await questionnaire.patch(_id, {
                                  activated: true,
                                });
                                Message.success('問卷啟用成功');
                                setIsUpdate(true);
                              } catch (e) {
                                console.error(e);
                                Message.error('問卷啟用失敗');
                              }
                            }}
                          >
                            啟用
                          </Button>
                        </>
                      )}
                      <Button
                        type="primary"
                        warning
                        onClick={() => handleDelete(_id)}
                      >
                        刪除
                      </Button>
                    </>
                  )}
                </Box>
              );
            }}
          />
        </Table>
      </Box>
      {/* 新增/編輯Dialog */}
      <Dialog
        title={editType === 'create' ? '新增問卷' : '編輯問卷'}
        visible={visible}
        onClose={() => {
          setVisible(false);
          setFormValues(undefined);
          setCurrentCompany(undefined);
          form.resetFields();
        }}
        onCancel={() => {
          setVisible(false);
          setFormValues(undefined);
          setCurrentCompany(undefined);
          form.resetFields();
        }}
        onOk={createQuestionnaire}
      >
        <Box
          sx={{
            width: '600px',
            height: '600px',
            padding: '0 10px 10px 0',
            overflowY: 'auto',
          }}
        >
          {questionnaireOptions.companies.length !== 0 && (
            <Form
              form={form}
              onFieldsChange={handleFormFieldChange}
              labelCol={{
                span: 4,
              }}
              labelAlign="left"
              wrapperCol={{
                span: 20,
              }}
            >
              <Item
                label="填寫限制"
                name="filler"
                rules={[
                  {
                    required: true,
                    message: '請選擇填寫限制',
                  },
                ]}
              >
                <Radio.Group disabled={!editable}>
                  <Radio value="byHouse">一戶一票</Radio>
                  <Radio value="byPerson">一人一票</Radio>
                </Radio.Group>
              </Item>
              <Item
                label="負責單位"
                name="companyId"
                rules={[{ required: true, message: '請選擇負責單位' }]}
              >
                <Select
                  disabled={!editable}
                  onChange={(val) => {
                    setCurrentCompany(
                      questionnaireOptions.companies.find((c) => c._id === val)
                    );
                    form.setFieldsValue({
                      service:
                        questionnaireOptions.companies.find(
                          (c) => c._id === val
                        )?.service[0] || '',
                    });
                  }}
                >
                  {questionnaireOptions.companies.map((company) => (
                    <Select.Option key={company._id} value={company._id}>
                      {company.name}
                    </Select.Option>
                  ))}
                </Select>
              </Item>
              <Item
                label="服務"
                name="service"
                rules={[{ required: true, message: '請選擇服務' }]}
              >
                <Select disabled={!editable} placeholder="請先選擇負責單位">
                  {currentCompany &&
                    currentCompany.service.map((service) => {
                      const data = serviceData.data.find(
                        (d) => d.id === service
                      );
                      return data ? (
                        <Select.Option key={data.id} value={data.id}>
                          {data.name}
                        </Select.Option>
                      ) : null;
                    })}
                </Select>
              </Item>
              <Item
                label="住戶權重"
                name={['weights', 'resident']}
                rules={[{ required: true, message: '請輸入住戶權重' }]}
              >
                <InputNumber
                  disabled={!editable}
                  min={0}
                  max={100}
                  step={0.1}
                  style={{ width: '100%' }}
                />
              </Item>
              <Item
                label="管委會權重"
                name={['weights', 'committee']}
                rules={[{ required: true, message: '請輸入管委會權重' }]}
              >
                <InputNumber
                  disabled={!editable}
                  min={0}
                  max={100}
                  step={0.1}
                  style={{ width: '100%' }}
                />
              </Item>
              <Item label="題目">
                <Box>
                  {formValues &&
                    Object.values(formValues.question).map((_, index) => {
                      return (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '85% 1fr',
                            gap: '10px',
                          }}
                          key={Math.random()}
                        >
                          <Item
                            label={`${index + 1}`}
                            name={['question', `topic${index + 1}`]}
                            style={{ width: '100%', gridColumn: '1/2' }}
                            rules={[{ required: true, message: '請輸入題目' }]}
                          >
                            <Input.TextArea disabled={!editable} />
                          </Item>
                          {index !== 0 && (
                            <Button
                              disabled={!editable}
                              type="secondary"
                              onClick={() => {
                                setFormValues(() => {
                                  const currentValues = form.getFieldsValue();
                                  const temp = { ...currentValues.question };
                                  delete temp[`topic${index + 1}`];
                                  const newQuestion = {};
                                  Object.values(temp).forEach(
                                    (item, subIndex) => {
                                      newQuestion[`topic${subIndex + 1}`] =
                                        item;
                                    }
                                  );
                                  return {
                                    ...currentValues,
                                    question: newQuestion,
                                  };
                                });
                              }}
                            >
                              刪除
                            </Button>
                          )}
                        </Box>
                      );
                    })}
                  <Button
                    disabled={!editable}
                    type="secondary"
                    onClick={() => {
                      setFormValues((prev) => {
                        const currentValues = form.getFieldsValue();
                        const newQuestions = { ...currentValues.question };
                        const newIndex = Object.keys(newQuestions).length;
                        newQuestions[`topic${newIndex + 1}`] = '';
                        return {
                          ...prev,
                          ...currentValues,
                          question: newQuestions,
                        };
                      });
                    }}
                    style={{ width: '100%' }}
                  >
                    新增題目
                  </Button>
                </Box>
              </Item>
            </Form>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
